import type { JSX } from "react";
// Data → Logs.
//
// One table of integration runs (test + sync) across *all* integrations.
// Fully URL-driven: integration / status / kind / search / page all live in
// the query string, so the view is shareable and back-button-correct. The
// per-integration card's "View all logs →" deep-links here pre-filtered.

import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { api, type IntegrationLogRow } from "../api.js";
import { Field, Select, TextInput } from "../components/Form.js";
import { PageHeader } from "../components/PageHeader.js";
import { cn } from "../lib/utils.js";
import { Button } from "../components/ui/button.js";
import { Card } from "../components/ui/card.js";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table.js";
import { formatDuration, logStatusColor } from "../integrations/logFormat.js";

const PAGE_SIZE = 50;

export function DataLogs(): JSX.Element {
  const [params, setParams] = useSearchParams();
  const integration = params.get("integration") ?? "";
  const status = params.get("status") ?? "";
  const kind = params.get("kind") ?? "";
  const search = params.get("search") ?? "";
  const page = Math.max(1, Number(params.get("page")) || 1);
  const offset = (page - 1) * PAGE_SIZE;

  // Any filter change rewrites the query string and resets page to 1
  // (except a page change itself, which keeps the filters).
  const updateFilter = (k: string, v: string) => {
    const next = new URLSearchParams(params);
    if (v) next.set(k, v);
    else next.delete(k);
    next.delete("page");
    setParams(next);
  };
  const goToPage = (p: number) => {
    const next = new URLSearchParams(params);
    if (p <= 1) next.delete("page");
    else next.set("page", String(p));
    setParams(next);
  };

  // Integration picker options.
  const integrations = useQuery({ queryKey: ["integrations"], queryFn: () => api.integrations() });

  const logs = useQuery({
    queryKey: ["integration-logs", { integration, status, kind, search, offset }],
    queryFn: () =>
      api.integrationLogs({
        integration: integration || undefined,
        status: status || undefined,
        kind: kind || undefined,
        search: search || undefined,
        limit: PAGE_SIZE,
        offset,
      }),
    // Keep polling while any visible run is still in flight.
    refetchInterval: (q) => (q.state.data?.items.some((e) => e.status === "running") ? 2000 : false),
  });

  const items = logs.data?.items ?? [];
  const total = logs.data?.pagination.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const rangeFrom = total === 0 ? 0 : offset + 1;
  const rangeTo = Math.min(offset + PAGE_SIZE, total);

  return (
    <>
      <PageHeader title="Logs" />

      <Card className="mb-4 flex flex-wrap items-end gap-3 p-4 [&_label]:mb-0">
        <div className="min-w-[220px] flex-[2]">
          <Field label="Search (note / error)">
            <TextInput
              type="search"
              value={search}
              placeholder="Search note or error…"
              onChange={(e) => {
                updateFilter("search", e.target.value);
              }}
            />
          </Field>
        </div>
        <div className="min-w-[180px] flex-1">
          <Field label="Integration">
            <Select
              value={integration}
              onChange={(e) => {
                updateFilter("integration", e.target.value);
              }}
            >
              <option value="">All integrations</option>
              {integrations.data?.items.map((row) => (
                <option key={row.id} value={row.id}>
                  {row.label && row.label !== row.title ? `${row.title} — ${row.label}` : row.title}
                </option>
              ))}
            </Select>
          </Field>
        </div>
        <div className="w-full sm:w-36">
          <Field label="Status">
            <Select
              value={status}
              onChange={(e) => {
                updateFilter("status", e.target.value);
              }}
            >
              <option value="">All</option>
              <option value="running">running</option>
              <option value="ok">ok</option>
              <option value="error">error</option>
            </Select>
          </Field>
        </div>
        <div className="w-full sm:w-32">
          <Field label="Kind">
            <Select
              value={kind}
              onChange={(e) => {
                updateFilter("kind", e.target.value);
              }}
            >
              <option value="">All</option>
              <option value="test">test</option>
              <option value="sync">sync</option>
            </Select>
          </Field>
        </div>
      </Card>

      {logs.isLoading && <div className="text-muted-foreground text-sm">Loading…</div>}
      {logs.isError && <div className="text-bad text-sm">Failed to load logs.</div>}

      {!logs.isLoading && items.length === 0 && (
        <Card className="p-5">
          <div className="text-muted-foreground text-sm">No log entries match these filters.</div>
        </Card>
      )}

      {items.length > 0 && (
        <Card className="overflow-hidden p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Integration</TableHead>
                <TableHead>Kind</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Started</TableHead>
                <TableHead className="text-right">Duration</TableHead>
                <TableHead className="text-right">Updated</TableHead>
                <TableHead>Note / Error</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((e) => (
                <LogTableRow key={e.id} entry={e} />
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      {total > 0 && (
        <div className="mt-3 flex items-center justify-between gap-3">
          <div className="text-muted-foreground text-sm">
            Showing {rangeFrom}–{rangeTo} of {total}
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                goToPage(page - 1);
              }}
              disabled={page <= 1}
            >
              ← Prev
            </Button>
            <span className="text-muted-foreground text-sm">
              Page {page} / {totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                goToPage(page + 1);
              }}
              disabled={page >= totalPages}
            >
              Next →
            </Button>
          </div>
        </div>
      )}
    </>
  );
}

function LogTableRow({ entry }: { entry: IntegrationLogRow }): JSX.Element {
  const [open, setOpen] = useState(false);
  const name =
    entry.integration_label && entry.integration_label !== entry.integration_title
      ? `${entry.integration_title} — ${entry.integration_label}`
      : entry.integration_title;
  const message = entry.note ?? entry.error ?? "";
  const flagged = entry.data.reconciliation?.flagged ?? false;
  return (
    <>
      <TableRow
        onClick={() => {
          setOpen((v) => !v);
        }}
        className="cursor-pointer"
      >
        <TableCell>{name}</TableCell>
        <TableCell className="text-muted-foreground">{entry.kind}</TableCell>
        <TableCell className={cn("font-semibold", logStatusColor(entry.status))}>
          {entry.status}
        </TableCell>
        <TableCell className="text-muted-foreground">
          {new Date(entry.started_at).toLocaleString()}
        </TableCell>
        <TableCell className="text-muted-foreground text-right">
          {formatDuration(entry.duration_ms)}
        </TableCell>
        <TableCell className="text-muted-foreground text-right">{entry.updated ?? "—"}</TableCell>
        <TableCell
          className={cn(
            "max-w-[360px] truncate",
            entry.error ? "text-bad" : "text-muted-foreground",
          )}
        >
          {flagged && (
            <span className="text-bad mr-1 font-semibold" title="Reconciliation discrepancy over threshold">
              ⚠ discrepancy
            </span>
          )}
          {message}
        </TableCell>
      </TableRow>
      {open && (
        <TableRow className="hover:bg-transparent">
          <TableCell colSpan={7} className="bg-muted whitespace-normal">
            <pre className="text-muted-foreground m-0 overflow-auto text-[11px]">
              {JSON.stringify(
                {
                  integration: entry.integration_code,
                  note: entry.note,
                  error: entry.error,
                  triggered_by: entry.triggered_by,
                  actor: entry.actor,
                  data: entry.data,
                },
                null,
                2,
              )}
            </pre>
          </TableCell>
        </TableRow>
      )}
    </>
  );
}
